/* Product page behaviour: copy buttons, quantity stepper, sticky answer
   strip and mobile buy bar (IntersectionObserver on the buy box), "Copy
   specs", folds forced open on desktop, in-page anchors that open their
   target fold before the browser scrolls to it. The gallery (thumbnails,
   magnifier, lightbox) lives in component-stoklink-gallery.js, the ladder
   toggle in component-stoklink-ladder.js, the quote modal in
   component-stoklink-quote-modal.js.
   Every visible string comes from data attributes written by Liquid. */
(function () {
  'use strict';

  var root = document.querySelector('[data-product]');
  if (!root) return;

  var section = root.closest('.shopify-section') || document;
  var desktop = window.matchMedia('(min-width: 1024px)');

  /* ---------- Clipboard ---------- */
  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
        resolve();
      } catch (error) {
        reject(error);
      }
      document.body.removeChild(area);
    });
  }

  function flashCopied(button) {
    var label = button.querySelector('[data-copy-text]');
    var copiedLabel = button.getAttribute('data-copied-label');
    button.classList.add('is-copied');
    if (label && copiedLabel) {
      if (!label.getAttribute('data-original')) label.setAttribute('data-original', label.textContent);
      label.textContent = copiedLabel;
    }
    clearTimeout(button._copyTimer);
    button._copyTimer = setTimeout(function () {
      button.classList.remove('is-copied');
      if (label && label.getAttribute('data-original')) label.textContent = label.getAttribute('data-original');
    }, 1600);
  }

  section.addEventListener('click', function (event) {
    var button = event.target.closest('[data-copy]');
    if (!button) return;
    copyText(button.getAttribute('data-copy') || '').then(function () {
      flashCopied(button);
    });
  });

  /* ---------- Copy specs: tab-separated label/value lines ---------- */
  var copySpecs = section.querySelector('[data-copy-specs]');
  if (copySpecs) {
    copySpecs.addEventListener('click', function () {
      var lines = [];
      section.querySelectorAll('[data-specs] [data-spec-row]').forEach(function (row) {
        var label = row.querySelector('.defrow__label');
        var value = row.querySelector('.defrow__value');
        if (!label || !value) return;
        lines.push(
          label.textContent.replace(/\s+/g, ' ').trim() + '\t' + value.textContent.replace(/\s+/g, ' ').trim()
        );
      });
      copyText(lines.join('\n')).then(function () {
        flashCopied(copySpecs);
      });
    });
  }

  /* ---------- Quantity stepper ---------- */
  section.querySelectorAll('[data-qty]').forEach(function (stepper) {
    var input = stepper.querySelector('[data-qty-input]');
    if (!input) return;
    function set(delta) {
      var min = parseInt(input.min, 10) || 1;
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = min;
      value = Math.max(min, value + delta);
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var dec = stepper.querySelector('[data-qty-dec]');
    var inc = stepper.querySelector('[data-qty-inc]');
    if (dec) dec.addEventListener('click', function () { set(-1); });
    if (inc) inc.addEventListener('click', function () { set(1); });
    input.addEventListener('blur', function () { set(0); });
  });

  /* ---------- Live total beside the stepper ----------
     Shown from quantity 2: unit price × quantity, formatted like the unit
     price text on the page (prefix/suffix, thousands and decimal separators
     are read from data-unit-text, so every presentment currency works). */
  var totalBox = section.querySelector('[data-qty-total]');
  var totalInput = section.querySelector('[data-qty-input]');
  if (totalBox && totalInput) {
    var unitCents = parseInt(totalBox.getAttribute('data-unit'), 10) || 0;
    var unitText = (totalBox.getAttribute('data-unit-text') || '').trim();
    var lineTemplate = totalBox.getAttribute('data-line-template') || '';
    var valueEl = totalBox.querySelector('[data-total-value]');
    var lineEl = totalBox.querySelector('[data-total-line]');

    function moneyShape(text) {
      var match = /[\d][\d.,'\s ]*/.exec(text);
      if (!match) return null;
      var number = match[0];
      var prefix = text.slice(0, match.index);
      var suffix = text.slice(match.index + number.length);
      var decimals = 0;
      var decimalSep = '';
      var tail = /([.,])(\d{1,2})$/.exec(number);
      if (tail) {
        decimalSep = tail[1];
        decimals = tail[2].length;
      }
      var body = decimalSep ? number.slice(0, number.length - decimals - 1) : number;
      var groupMatch = /[^\d]/.exec(body);
      var groupSep = groupMatch ? groupMatch[0] : '';
      return { prefix: prefix, suffix: suffix, decimals: decimals, decimalSep: decimalSep, groupSep: groupSep };
    }

    function formatMoney(cents, shape) {
      var units = Math.round(cents) / 100;
      var fixed = units.toFixed(shape.decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, shape.groupSep);
      var out = whole;
      if (shape.decimals > 0) out += shape.decimalSep + parts[1];
      return shape.prefix + out + shape.suffix;
    }

    var shape = moneyShape(unitText);
    function updateTotal() {
      var qty = parseInt(totalInput.value, 10);
      if (!shape || !unitCents || isNaN(qty) || qty < 2) {
        totalBox.hidden = true;
        return;
      }
      if (valueEl) {
        valueEl.textContent = formatMoney(unitCents * qty, shape);
        /* The CSS sizes the amount from its length (see .buybox__total-value). */
        valueEl.style.setProperty('--chars', String(valueEl.textContent.length));
      }
      if (lineEl) lineEl.textContent = lineTemplate.replace('[qty]', String(qty)).replace('[unit]', unitText);
      totalBox.hidden = false;
    }
    totalInput.addEventListener('input', updateTotal);
    totalInput.addEventListener('change', updateTotal);
    updateTotal();
  }

  /* ---------- Sticky answer strip (desktop) and mobile buy bar ---------- */
  var strip = root.querySelector('[data-answer-strip]');
  var buybox = root.querySelector('[data-buybox]');
  var bar = section.querySelector('[data-mobile-bar]');
  var lastEntry = null;

  function applyStickiness() {
    if (!lastEntry) return;
    var passed = !lastEntry.isIntersecting && lastEntry.boundingClientRect.bottom < 0;
    if (strip) strip.classList.toggle('is-stuck', passed && desktop.matches);
    if (bar) bar.classList.toggle('is-hidden', lastEntry.isIntersecting);
  }

  if (buybox && 'IntersectionObserver' in window) {
    var observer = new IntersectionObserver(function (entries) {
      lastEntry = entries[entries.length - 1];
      applyStickiness();
    }, { threshold: 0 });
    observer.observe(buybox);
    desktop.addEventListener('change', applyStickiness);
  }

  /* ---------- Folds: accordions on mobile, always open on desktop ---------- */
  var foldsCollapsed = false;
  function syncFolds() {
    var folds = section.querySelectorAll('details[data-fold]');
    if (desktop.matches) {
      folds.forEach(function (fold) { fold.open = true; });
      return;
    }
    if (foldsCollapsed) return;
    foldsCollapsed = true;
    folds.forEach(function (fold, index) { fold.open = index === 0 || fold.id === 'ladder'; });
  }
  syncFolds();
  desktop.addEventListener('change', syncFolds);

  /* ---------- In-page anchors open their fold first ----------
     The target of "#specs", "#ladder", "#documents" … is a <details data-fold>
     (or sits inside one). Opening it synchronously in the click handler lets
     the browser's own fragment navigation scroll to an expanded fold; the
     hashchange listener covers history navigation and links from outside the
     section; the initial call covers a page opened with a hash. */
  function openFoldFor(hash) {
    if (!hash || hash.length < 2) return;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (error) {
      id = hash.slice(1);
    }
    var target = document.getElementById(id);
    if (!target) return;
    /* Every <details> around the target: the fold, and on the specification
       sheet the "Show all" rest and the manufacturer-data accordion. */
    var fold = target.closest('details');
    while (fold) {
      if (!fold.open) fold.open = true;
      fold = fold.parentElement ? fold.parentElement.closest('details') : null;
    }
  }

  /* ---------- Specification sheet ----------
     Groups after the first three sit in <details data-spec-rest>, the
     manufacturer's extra rows in <details data-spec-mfr> inside it. A group
     chip is a plain anchor: the click handler below opens the <details>
     around its target before the browser scrolls. The select (phones) does
     the same and scrolls itself. Chips are filled while their group is
     visible; the foot's "Show less" (JS only) closes the rest again. */
  var sheet = section.querySelector('[data-spec-sheet]');
  if (sheet) {
    var specRest = sheet.querySelector('[data-spec-rest]');
    var specMfr = sheet.querySelector('[data-spec-mfr]');
    var specChips = section.querySelectorAll('[data-spec-jump]');
    var specLess = sheet.querySelector('[data-spec-less]');
    var specSelect = section.querySelector('[data-spec-select]');

    function syncSpecNav() {
      var restOpen = !!(specRest && specRest.open);
      var mfrOpen = !!(specMfr && specMfr.open);
      specChips.forEach(function (chip) {
        var state = chip.getAttribute('data-spec-state');
        var open = state === 'core' || (state === 'rest' && restOpen) || (state === 'mfr' && restOpen && mfrOpen);
        chip.classList.toggle('is-open', open);
      });
      if (specLess) specLess.hidden = !restOpen;
    }

    if (specRest) specRest.addEventListener('toggle', syncSpecNav);
    if (specMfr) specMfr.addEventListener('toggle', syncSpecNav);

    if (specSelect) {
      var selectByKeys = false;

      function jumpToSelected() {
        var key = specSelect.value;
        if (!key) return;
        var hash = '#spec-' + key;
        openFoldFor(hash);
        var group = document.getElementById('spec-' + key);
        if (!group) return;
        group.scrollIntoView({ block: 'start' });
        history.replaceState(history.state, '', hash);
        if (!group.hasAttribute('tabindex')) group.setAttribute('tabindex', '-1');
        group.focus({ preventScroll: true });
      }

      /* A closed select changes on every arrow key (Chrome, Firefox), so a
         keyboard user picks with the arrows and confirms with Enter; a
         pointer or the phone's picker fires one change, which jumps at once.
         The select keeps showing the group it jumped to. */
      specSelect.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          selectByKeys = false;
          jumpToSelected();
        } else if (event.key.indexOf('Arrow') === 0 || event.key === 'Home' || event.key === 'End' || event.key === 'PageUp' || event.key === 'PageDown') {
          selectByKeys = true;
        }
      });
      specSelect.addEventListener('blur', function () {
        selectByKeys = false;
      });
      specSelect.addEventListener('change', function () {
        if (selectByKeys) return;
        jumpToSelected();
      });
    }

    if (specLess && specRest) {
      specLess.addEventListener('click', function () {
        specRest.open = false;
        if (specMfr) specMfr.open = false;
        sheet.scrollIntoView({ block: 'start' });
        history.replaceState(history.state, '', '#specs');
        var summary = specRest.querySelector('summary');
        if (summary) summary.focus({ preventScroll: true });
        syncSpecNav();
      });
    }

    /* Print the whole sheet, then put the accordions back as they were. */
    var printState = null;
    window.addEventListener('beforeprint', function () {
      printState = { rest: !!(specRest && specRest.open), mfr: !!(specMfr && specMfr.open) };
      if (specRest) specRest.open = true;
      if (specMfr) specMfr.open = true;
    });
    window.addEventListener('afterprint', function () {
      if (!printState) return;
      if (specRest) specRest.open = printState.rest;
      if (specMfr) specMfr.open = printState.mfr;
      printState = null;
    });

    syncSpecNav();
  }

  section.addEventListener('click', function (event) {
    var link = event.target.closest('a[href^="#"]');
    if (!link) return;
    openFoldFor(link.getAttribute('href'));
  });

  window.addEventListener('hashchange', function () {
    openFoldFor(window.location.hash);
  });

  openFoldFor(window.location.hash);
})();
